import { useEffect, useRef, useState } from 'react';
import {
  ArrowUpTrayIcon,
  DocumentArrowUpIcon,
  PlusIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import SmartInputBox from '../../AutomationPage/SmartInputBox';
import QuickFileUploadModal from '../../(Automation)/RecordPage/QuickFileUploadModal';
import ButtonComponent from '../../customButton';
import { useSnackbar } from '../../../utils/useSnackbar';
import { serializeInstructionsToClipboard, parseInstructionClipboard } from '../../../utils/automation/instructionClipboard';
import styles from '../styles/automationNew.module.scss';
import StatusPill from './StatusPill';

function createBlankInstruction(number = 1) {
  return {
    id: `inst-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    number,
    text: '',
    status: 'pending',
    expanded: false,
    children: [],
  };
}

function renumber(list) {
  return list.map((inst, index) => ({ ...inst, number: index + 1 }));
}

/** Block stacking blank rows (same rule as Automation Studio / QPN-2017). */
function canAddBelow(instructions, index) {
  const current = instructions[index];
  if (!current) return false;
  if (!String(current.text ?? '').trim()) return false;
  const next = instructions[index + 1];
  if (!next) return true;
  return String(next.text ?? '').trim().length > 0;
}

function parseInstructionFile(raw, fileName = '') {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return [];

  const looksJson =
    fileName.toLowerCase().endsWith('.json') || trimmed.startsWith('[') || trimmed.startsWith('{');
  if (looksJson) {
    const parsed = JSON.parse(trimmed);
    const list = Array.isArray(parsed)
      ? parsed
      : Array.isArray(parsed?.instructions)
        ? parsed.instructions
        : null;
    if (!list) {
      throw new Error('JSON must be an array of instructions or { instructions: [] }');
    }
    return list
      .map((item) => (typeof item === 'string' ? item : item?.text || item?.instruction || ''))
      .map((text) => String(text || '').trim())
      .filter(Boolean);
  }

  return trimmed
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function autosizeTextarea(el) {
  if (!el) return;
  el.style.height = '0px';
  el.style.height = `${Math.max(40, el.scrollHeight)}px`;
}

export default function InstructionComposer({
  instructions = [],
  isRunning = false,
  onInstructionsChange,
  onClearInstructions,
  variableSuggestions = [],
  variableDescriptions = {},
  fileSuggestions = [],
  fileDescriptions = {},
  onCollapsedChange,
  onFileUploaded,
}) {
  const fileInputRef = useRef(null);
  const pendingFocusIndexRef = useRef(null);
  const wasRunningRef = useRef(false);
  const { showSnackbar } = useSnackbar();
  const [importError, setImportError] = useState('');
  const [importInfo, setImportInfo] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    if (isRunning && !wasRunningRef.current) {
      setCollapsed(true);
    } else if (!isRunning && wasRunningRef.current) {
      setCollapsed(false);
    }
    wasRunningRef.current = isRunning;
  }, [isRunning]);

  useEffect(() => {
    onCollapsedChange?.(collapsed);
  }, [collapsed, onCollapsedChange]);

  useEffect(() => {
    if (isRunning) return;
    if (instructions.length === 0) {
      onInstructionsChange?.([createBlankInstruction(1)]);
    }
  }, [instructions.length, isRunning, onInstructionsChange]);

  useEffect(() => {
    const index = pendingFocusIndexRef.current;
    if (index == null) return;
    pendingFocusIndexRef.current = null;
    requestAnimationFrame(() => {
      const root = document.querySelector(`[data-anew-instruction-wrap="${index}"]`);
      const el = root?.querySelector?.('textarea, input');
      if (el && typeof el.focus === 'function') {
        el.focus();
        const len = el.value?.length ?? 0;
        el.setSelectionRange?.(len, len);
        autosizeTextarea(el);
      }
    });
  }, [instructions]);

  useEffect(() => {
    instructions.forEach((_, index) => {
      const root = document.querySelector(`[data-anew-instruction-wrap="${index}"]`);
      const el = root?.querySelector?.('textarea, input');
      autosizeTextarea(el);
    });
  }, [instructions]);

  const commit = (next) => {
    onInstructionsChange?.(renumber(next));
  };

  const handleTextChange = (index, text) => {
    if (isRunning) return;
    const next = instructions.map((inst, i) => (i === index ? { ...inst, text } : inst));
    commit(next);
    if (importError) setImportError('');
  };

  const handleAddBelow = (index) => {
    if (isRunning || !canAddBelow(instructions, index)) return;
    const next = [...instructions];
    next.splice(index + 1, 0, createBlankInstruction(index + 2));
    pendingFocusIndexRef.current = index + 1;
    commit(next);
  };

  const handleDelete = (index) => {
    if (isRunning) return;
    if (instructions.length <= 1) {
      pendingFocusIndexRef.current = 0;
      commit([createBlankInstruction(1)]);
      return;
    }
    const next = instructions.filter((_, i) => i !== index);
    pendingFocusIndexRef.current = Math.min(index, next.length - 1);
    commit(next);
  };

  const handleClearAll = () => {
    if (isRunning) return;
    onClearInstructions?.();
    pendingFocusIndexRef.current = 0;
    setImportError('');
    setImportInfo('');
    showSnackbar('Instructions cleared', 'info');
  };

  const handleCopyInstructions = () => {
    const text = serializeInstructionsToClipboard(instructions);
    navigator.clipboard.writeText(text).then(() => {
      showSnackbar('Instructions copied to clipboard', 'success');
    }).catch(() => {
      showSnackbar('Failed to copy instructions', 'error');
    });
  };

  const handlePasteInstructions = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const { valid, instructions: parsed } = parseInstructionClipboard(text);

      let newInstructions = [];

      if (valid && parsed?.length) {
        newInstructions = parsed.map((item, index) => ({
          ...createBlankInstruction(index + 1),
          text: item.text,
        }));
      } else {
        const lines = text
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean);

        if (!lines.length) {
          showSnackbar('Invalid format or empty clipboard', 'warning');
          return;
        }

        newInstructions = lines.map((line, index) => ({
          ...createBlankInstruction(index + 1),
          text: line,
        }));
      }

      pendingFocusIndexRef.current = 0;
      commit(newInstructions);
      showSnackbar(`Loaded ${newInstructions.length} instruction${newInstructions.length === 1 ? '' : 's'}`, 'success');
    } catch {
      showSnackbar('Could not read clipboard', 'error');
    }
  };

  const handleKeyDown = (event, index) => {
    if (isRunning) return;
    if (event.key === 'Enter' && !event.shiftKey) {
      if (document.querySelector('[class*="variableDropdownPortal"]')) return;
      event.preventDefault();
      if (canAddBelow(instructions, index)) {
        handleAddBelow(index);
      }
    }
  };

  const handlePaste = (event, index) => {
    if (isRunning) return;
    const text = event.clipboardData?.getData('text') || '';
    if (!text.includes('\n')) return;

    event.preventDefault();
    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    if (lines.length === 0) return;

    const next = [...instructions];
    next[index] = { ...next[index], text: lines[0] };
    const extras = lines.slice(1).map((line, offset) => ({
      ...createBlankInstruction(index + 2 + offset),
      text: line,
    }));
    next.splice(index + 1, 0, ...extras);
    pendingFocusIndexRef.current = index + lines.length - 1;
    commit(next);
    const msg = `Pasted ${lines.length} instructions`;
    setImportInfo(msg);
    showSnackbar(msg, 'success');
  };

  const handleImportClick = () => {
    if (isRunning) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      const raw = await file.text();
      const lines = parseInstructionFile(raw, file.name);
      if (lines.length === 0) {
        const msg = 'No instructions found in that file.';
        setImportError(msg);
        setImportInfo('');
        showSnackbar(msg, 'error');
        return;
      }
      const next = lines.map((text, index) => ({
        ...createBlankInstruction(index + 1),
        text,
      }));
      pendingFocusIndexRef.current = 0;
      commit(next);
      setImportError('');
      const msg = `Imported ${lines.length} instruction${lines.length === 1 ? '' : 's'} from ${file.name}`;
      setImportInfo(msg);
      showSnackbar(msg, 'success');
    } catch (error) {
      const msg = error?.message || 'Could not read that file. Use .txt (one per line) or .json.';
      setImportError(msg);
      setImportInfo('');
      showSnackbar(msg, 'error');
    }
  };

  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    onFileUploaded?.();
    showSnackbar('File uploaded — use @ to insert it', 'success');
  };

  const filledCount = instructions.filter((inst) => String(inst.text || '').trim()).length;

  if (collapsed) {
    return (
      <section className={`${styles.panel} ${styles.panelCollapsed}`} aria-label="Instruction Composer">
        <div className={styles.collapsedShell}>
          <button
            type="button"
            className={styles.collapseToggle}
            onClick={() => setCollapsed(false)}
            aria-expanded={false}
            aria-label="Expand instructions"
            title="Expand instructions"
          >
            <span aria-hidden>›</span>
          </button>
          <div className={styles.collapsedRail}>
            <span className={styles.composerCountCollapsed} title={`${filledCount} instructions`}>
              {filledCount}
            </span>
            <span className={styles.collapsedRailLabel}>Instructions</span>
            {isRunning && <span className={styles.collapsedRailLive}>LIVE</span>}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.panel} aria-label="Instruction Composer">
      <div className={styles.panelHeader}>
        <div className={styles.composerHeaderLeft}>
          <button
            type="button"
            className={styles.collapseToggle}
            onClick={() => setCollapsed(true)}
            aria-expanded
            aria-label="Collapse instructions"
            title="Collapse instructions"
          >
            <span aria-hidden>‹</span>
          </button>
          <h2 className={styles.composerHeading}>Instructions</h2>
          <span className={styles.composerCount}>
            {filledCount} ready{isRunning ? ' · locked' : ''}
          </span>
        </div>
        <div className={styles.composerToolbar}>
          {filledCount > 0 && (
            <ButtonComponent
              variant="secondary"
              size="small"
              className="slate900 bg-slate50"
              startIcon={<ClipboardDocumentIcon className="icon slate900" style={{ width: 14, height: 14 }} />}
              onClick={handleCopyInstructions}
              disabled={isRunning}
              type="iconbutton"
              tooltip="Copy instructions"
            />
          )}
          {filledCount === 0 && (
            <ButtonComponent
              variant="secondary"
              size="small"
              className="slate900 bg-slate50"
              startIcon={<ClipboardDocumentListIcon className="icon slate900" style={{ width: 14, height: 14 }} />}
              onClick={handlePasteInstructions}
              disabled={isRunning}
              type="iconbutton"
              tooltip="Paste instructions"
            />
          )}
          <ButtonComponent
            ctaText="Upload"
            variant="outline"
            size="small"
            className="primary"
            startIcon={<ArrowUpTrayIcon className="icon" style={{ width: 14, height: 14 }} />}
            onClick={() => setShowUploadModal(true)}
            disabled={isRunning}
            tooltip="Upload execution file"
          />
          <ButtonComponent
            ctaText="Import"
            variant="outline"
            size="small"
            className="primary"
            startIcon={<DocumentArrowUpIcon className="icon" style={{ width: 14, height: 14 }} />}
            onClick={handleImportClick}
            disabled={isRunning}
            tooltip="Import .txt (one per line) or .json"
          />
          <ButtonComponent
            ctaText="Clear"
            variant="outline"
            size="small"
            className="primary"
            onClick={handleClearAll}
            disabled={isRunning || filledCount === 0}
            tooltip="Clear all instructions"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,.json,text/plain,application/json"
            hidden
            onChange={handleFileSelected}
          />
        </div>
      </div>

      {isRunning && (
        <div className={styles.runningBanner} role="status">
          Automation is running — instructions are locked.
        </div>
      )}

      <div className={`${styles.panelBody} ${styles.composerBody}`}>
        <div className={styles.instructionEditorList}>
          {instructions.map((instruction, index) => {
            const canAdd = !isRunning && canAddBelow(instructions, index);
            const isFocused = focusedIndex === index;
            return (
              <div
                key={instruction.id}
                className={`${styles.instructionEditorRow} ${
                  isFocused ? styles.instructionEditorRowFocused : ''
                }`}
              >
                <StatusPill status={instruction.status} label={instruction.number} number />

                <div
                  className={styles.instructionEditorField}
                  data-anew-instruction-wrap={index}
                  onPaste={(event) => handlePaste(event, index)}
                >
                  <SmartInputBox
                    size="small"
                    value={instruction.text}
                    disabled={isRunning}
                    placeholder="Type ${ for variables, @ for files"
                    customClassName={styles.instructionSmartInput}
                    variableTrigger="${"
                    suggestions={variableSuggestions}
                    descriptions={variableDescriptions}
                    fileTrigger="@"
                    fileSuggestions={fileSuggestions}
                    fileDescriptions={fileDescriptions}
                    formatFileInsertion={(file) => `@[${file.id}]`}
                    onChange={(event) => {
                      handleTextChange(index, event.target.value);
                      requestAnimationFrame(() => {
                        const root = document.querySelector(
                          `[data-anew-instruction-wrap="${index}"]`
                        );
                        autosizeTextarea(root?.querySelector?.('textarea, input'));
                      });
                    }}
                    onKeyDown={(event) => handleKeyDown(event, index)}
                    onFocus={() => setFocusedIndex(index)}
                    onBlur={() => setFocusedIndex((prev) => (prev === index ? null : prev))}
                  />
                  {instruction.expanded && instruction.children?.length > 0 && (
                    <ul className={styles.childList}>
                      {instruction.children.map((child) => (
                        <li key={child.id} className={styles.childItem}>
                          <span className={styles.childDot} aria-hidden />
                          <span>
                            {child.number}. {child.text}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className={styles.instructionEditorActions}>
                  <ButtonComponent
                    type="iconbutton"
                    variant="outline"
                    size="small"
                    startIcon={<PlusIcon className="icon" style={{ width: 14, height: 14 }} />}
                    onClick={() => handleAddBelow(index)}
                    disabled={!canAdd}
                    tooltip={canAdd ? 'Add instruction below' : 'Fill this step before adding another'}
                  />
                  <ButtonComponent
                    type="iconbutton"
                    variant="outline"
                    size="small"
                    className="red600"
                    startIcon={<TrashIcon className="icon red600" style={{ width: 14, height: 14 }} />}
                    onClick={() => handleDelete(index)}
                    disabled={isRunning}
                    tooltip={
                      instructions.length <= 1 ? 'Clear this instruction' : 'Remove instruction'
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={`${styles.panelFooter} ${styles.composerFooter}`}>
        {(importError || importInfo) && (
          <p className={importError ? styles.formError : styles.composerHintInline}>
            {importError || importInfo}
          </p>
        )}
        {!isRunning && (
          <p className={styles.composerHintInline}>
            Type {'${'} for variables · @ for files · Enter adds next · paste multiple lines
          </p>
        )}
      </div>

      {showUploadModal && (
        <QuickFileUploadModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
        />
      )}
    </section>
  );
}
